/**
 * useGiftCardListState — data + filter/pagination state for the gift-cards
 * list (BON-223), split out of the page so the page stays a thin view (the
 * `useBookingsList` / `useReviewQueueState` pattern).
 *
 * Search, status and source are query params the SERVER resolves (the frozen
 * contract's `search`/`status`/`source`) — this hook only debounces the search
 * box, resets to page 1 on any filter change, and guards against a stale
 * response with AbortController.
 */
import { useEffect, useState } from 'react';

import { usePagination } from '../../hooks/usePagination';

import { fetchGiftCards } from './giftCardsApi';
import type {
  GiftCardListRow,
  GiftCardSourceFilter,
  GiftCardStatusFilter,
  GiftCardSummary,
} from './giftCardTypes';

/** Fixed page size — the design's 12-row pager, no size selector. */
export const PAGE_SIZE = 12;

/** Honest zeros while the first response hasn't landed yet — the KPI row
 *  reads these the same way it reads a real, all-zero result. */
const EMPTY_SUMMARY: GiftCardSummary = { totalCards: 0, totalIssuedCents: 0, totalBalanceCents: 0 };

export function useGiftCardListState() {
  const [rows, setRows] = useState<GiftCardListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<GiftCardSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatusState] = useState<GiftCardStatusFilter>('all');
  const [source, setSourceState] = useState<GiftCardSourceFilter>('all');

  const pager = usePagination(total, PAGE_SIZE);
  const { setOffset } = pager;

  // Debounce the search box → committed query, resetting to page 1 on commit —
  // same 300ms as CustomerManagement's search, so the two lists feel the same.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, setOffset]);

  const setStatus = (next: GiftCardStatusFilter) => {
    setStatusState(next);
    setOffset(0);
  };
  const setSource = (next: GiftCardSourceFilter) => {
    setSourceState(next);
    setOffset(0);
  };
  const clear = () => {
    setSearchInput('');
    setSearch('');
    setStatusState('all');
    setSourceState('all');
    setOffset(0);
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    setError(null);
    fetchGiftCards({ search, status, source, limit: PAGE_SIZE, offset: pager.offset }, signal)
      .then((res) => {
        if (signal.aborted) return;
        setRows(res.giftCards);
        setTotal(res.pagination.total);
        setSummary(res.summary);
      })
      .catch((err: unknown) => {
        if (signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
        setError(err instanceof Error ? err.message : 'Failed to load gift cards');
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [search, status, source, pager.offset]);

  return {
    rows,
    total,
    summary,
    loading,
    error,
    searchInput,
    setSearchInput,
    search,
    status,
    setStatus,
    source,
    setSource,
    clear,
    pager,
  };
}
